import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';

// GraphQL mutation for updating game status
export const UPDATE_GAME_MUTATION = gql`
  mutation UpdateGame($id: Int!, $input: UpdateGameInput!) {
    updateGame(id: $id, input: $input) {
      id
      name
      gameId
      providerId
      categoryId
      launchUrl
      demoAvailable
      freespinAvailable
      deviceType
      betType
      linesCount
      isActive
      iconUrl
      description
      orientation
      categoryType
      createdAt
      position
    }
  }
`;

// Types for the update game input
export interface UpdateGameInput {
  isActive?: boolean;
  name?: string;
  gameId?: string;
  providerId?: number;
  categoryId?: number;
  launchUrl?: string;
  demoAvailable?: boolean;
  freespinAvailable?: boolean;
  deviceType?: string;
  betType?: string;
  linesCount?: number;
  iconUrl?: string;
  description?: string;
  position?: number;
  orientation?: string
  categoryType?: string 
}

// Raw GraphQL response interface (before transformation)
export interface RawGame {
  id: string; // GraphQL ID fields are strings
  name: string;
  gameId: string;
  providerId: number;
  categoryId: number;
  launchUrl?: string | null;
  demoAvailable: boolean;
  freespinAvailable: boolean;
  deviceType: string;
  betType?: string | null;
  linesCount?: number | null;
  isActive: boolean;
  iconUrl?: string | null;
  description?: string | null;
  createdAt: string;
  position: number;
  orientation?: string
  categoryType?: string
}

// Transformed Game interface (after conversion)
export interface Game {
  id: number;
  name: string;
  gameId: string;
  providerId: number;
  categoryId: number;
  launchUrl?: string | null;
  demoAvailable: boolean;
  freespinAvailable: boolean;
  deviceType: string;
  betType?: string | null;
  linesCount?: number | null;
  isActive: boolean;
  iconUrl?: string | null;
  description?: string | null;
  createdAt: string;
  position: number;
  orientation?: string;
  categoryType?: string
}

export interface UpdateGameResponse {
  updateGame: RawGame;
}

// UpdateGameStatusById service class
export class UpdateGameStatusByIdService {
  private static instance: UpdateGameStatusByIdService;

  private constructor() { }

  public static getInstance(): UpdateGameStatusByIdService {
    if (!UpdateGameStatusByIdService.instance) {
      UpdateGameStatusByIdService.instance = new UpdateGameStatusByIdService();
    }
    return UpdateGameStatusByIdService.instance;
  }

  // Update game status by ID
  async updateGameStatusById(
    id: number,
    input: UpdateGameInput,
  ): Promise<Game> {
    try {
      const { data } = await apolloClient.mutate<UpdateGameResponse>({
        mutation: UPDATE_GAME_MUTATION,
        variables: { id, input },
      });

      if (!data?.updateGame) {
        throw new Error('No data returned from update game mutation');
      }

      // Transform the response to convert string ID to number
      const transformedGame = {
        ...data.updateGame,
        id: Number(data.updateGame.id),
      };

      return transformedGame;
    } catch (error: any) {
      console.error('Error updating game status:', error);
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }

  // Convenience method for updating just the active status
  async updateGameActiveStatus(id: number, isActive: boolean): Promise<Game> {
    return this.updateGameStatusById(id, { isActive });
  }
}

// Export singleton instance
export const updateGameStatusByIdService =
  UpdateGameStatusByIdService.getInstance();

// Export convenience function
export async function updateGameStatusById(
  id: number,
  input: UpdateGameInput,
): Promise<Game> {
  return updateGameStatusByIdService.updateGameStatusById(id, input);
}

// Export convenience function for updating active status
export async function updateGameActiveStatus(
  id: number,
  isActive: boolean,
): Promise<Game> {
  return updateGameStatusByIdService.updateGameActiveStatus(id, isActive);
}

export const updateGameOrientation = async (
  id: number,
  orientation: string,
): Promise<Game> => {
  return updateGameStatusByIdService.updateGameStatusById(id, { orientation });
};
export const updateGameCategoryType = async (
  id: number,
  categoryType: string,
): Promise<Game> => {
  return updateGameStatusByIdService.updateGameStatusById(id, { categoryType });
};
